import React, { useEffect, useRef, useState } from "react";

import AddSection from "../pages/AddSection";
import ContractSectionTabs from "../pages/ContractSectionTabs";
import TenderDetails from "../pages/TenderDetails";
import StageItemForm from "../pages/StageItemForm";
import { useParams } from "react-router-dom";

type ActiveMenu = "section" | "item" | null;

type ItemType =
  | "TradeItem"
  | "StageItem"
  | "PriceItem"
  | null;

export interface PageLayoutHeaderActions {
  onAddSection: () => void;
  onAddItem: () => void;
}

interface PageLayoutProps {
  onRegisterHeaderActions?: (
    actions: PageLayoutHeaderActions | null
  ) => void;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  onRegisterHeaderActions,
}) => {
  const [activeMenu, setActiveMenu] =
    useState<ActiveMenu>(null);

  // Used to refresh displayed sections after saving
  const [refreshKey, setRefreshKey] = useState(0);

  // Selected contract section
  const [selectedSection, setSelectedSection] =
    useState<number | null>(null);

  // Selected contract node
  const [selectedContractNode, setSelectedContractNode] =
    useState<any>(null);

  // Parent node for Add Section
  const [parentNode, setParentNode] =
    useState<number | null>(null);

  const { tenderNo } = useParams<{ tenderNo: string }>();

const tenderNumber = Number(tenderNo);

  // Currently displayed item type. This is cleared when a popup closes.
  const [selectedItemType, setSelectedItemType] =
    useState<ItemType>(null);

  // Remember the last item type explicitly selected by the user, so clicking
  // + Item again can reopen that form instead of showing Select Item Type.
  const [lastSelectedItemType, setLastSelectedItemType] =
    useState<ItemType>(null);

  // Selected Trade Item RateGroupNo
  const [selectedRateGroupNo, setSelectedRateGroupNo] =
    useState<number | null>(null);

  // WBS number associated with the selected Trade Item
const [selectedWbsNo, setSelectedWbsNo] =
  useState<number | null>(null);

  
const [parentRateType, setParentRateType] =
  useState<"Trade Item" | "Stage Item">("Trade Item");

  // Item popup mode
  //
  // rateGroup = normal Trade Item
  // subItem   = Add Sub Item
  // workGroup = Add Sub Works
  const [itemMode, setItemMode] =
    useState<"rateGroup" | "subItem" | "workGroup">(
      "rateGroup"
    );

  /*
  |--------------------------------------------------------------------------
  | Close Popup
  |--------------------------------------------------------------------------
  */

  const closePopup = () => {
    setActiveMenu(null);
    setSelectedItemType(null);
    setSelectedRateGroupNo(null);
    setSelectedWbsNo(null);
    setParentRateType("Trade Item");
    // Always return to normal Add Item mode
    setItemMode("rateGroup");

     
  };

  /*
  |--------------------------------------------------------------------------
  | Open Add Section popup
  |--------------------------------------------------------------------------
  */

  const handleAddSection = (
    parent: number | null
  ) => {
    setParentNode(parent);

    setActiveMenu("section");

    setSelectedItemType(null);
  };

  /*
  |--------------------------------------------------------------------------
  | After AddSection saves successfully
  |--------------------------------------------------------------------------
  */

  const handleSectionSaved = () => {
    closePopup();

    // Reload displayed section list
    setRefreshKey(
      (previous) => previous + 1
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Open Add Item popup from left menu
  |--------------------------------------------------------------------------
  */

  const handleAddItem = () => {
    // Preserve the currently selected contract section.
    // Stage Items must be linked to a valid contractNodeNo.
    // If no section is selected, select a section first.

    // Normal Add Item mode
    setSelectedRateGroupNo(null);
    setSelectedWbsNo(null);
    setParentRateType("Trade Item");
    setItemMode("rateGroup");

    setActiveMenu("item");
    // Reopen the last explicitly selected item type, if available.
    setSelectedItemType(lastSelectedItemType);
  };

  // Keep the latest header actions available to SecondScreenLayout.
  const headerActionsRef = useRef<PageLayoutHeaderActions>({
    onAddSection: () => {},
    onAddItem: () => {},
  });

  headerActionsRef.current = {
    onAddSection: () => {
      setParentNode(null);
      setActiveMenu("section");
      setSelectedItemType(null);
    },
    onAddItem: handleAddItem,
  };

  useEffect(() => {
    if (!onRegisterHeaderActions) return;

    onRegisterHeaderActions({
      onAddSection: () => headerActionsRef.current.onAddSection(),
      onAddItem: () => headerActionsRef.current.onAddItem(),
    });

    return () => onRegisterHeaderActions(null);
  }, [onRegisterHeaderActions]);

  /*
  |--------------------------------------------------------------------------
  | Open Add Item popup for a specific section
  |--------------------------------------------------------------------------
  */

  const handleAddItemForSection = (
    node: any
  ) => {
    // Select the section first
    setSelectedSection(
      node.contractNodeNo
    );

    // Store the complete selected section
    setSelectedContractNode(node);

    // Normal Trade Item mode
    setSelectedRateGroupNo(null);
    setItemMode("rateGroup");

    // Open the same Add Item popup. Keep the previously selected type
    // so the user does not have to choose it every time.
    setActiveMenu("item");
    setSelectedItemType(lastSelectedItemType);
  };

  /*
  |--------------------------------------------------------------------------
  | Open Add Sub Item
  |--------------------------------------------------------------------------
  */

  
/* const handleAddSubItem = (
  rateGroupNo: number,
  wbsNo: number | null
) => {
  console.log("Opening Sub Item for RateGroup:", rateGroupNo);
  console.log("Selected Trade Item WBS:", wbsNo);

  // Store the selected Trade Item
  setSelectedRateGroupNo(rateGroupNo);

  // Store its associated WBS number
  setSelectedWbsNo(wbsNo);

  // Switch to Sub Item mode
  setItemMode("subItem");

  // Sub Item does not belong to a selected section
  setSelectedSection(null);
  setSelectedContractNode(null);

  // Open the item popup
  setActiveMenu("item");
  setSelectedItemType("TradeItem");
}; */

const handleAddSubItem = (
  rateGroupNo: number,
  wbsNo: number | null,
  rateType: "Trade Item" | "Stage Item"
) => {
  console.log("Opening Sub Item for RateGroup:", rateGroupNo);
  console.log("Selected WBS:", wbsNo);
  console.log("Parent Rate Type:", rateType);

  // Store the selected parent item
  setSelectedRateGroupNo(rateGroupNo);
  setSelectedWbsNo(wbsNo);
  setParentRateType(rateType);

  // Switch to Sub Item mode
  setItemMode("subItem");

  // Clear the selected section and contract node
  setSelectedSection(null);
  setSelectedContractNode(null);

  // Open the Sub Item popup
  setActiveMenu("item");
  setSelectedItemType("TradeItem");
};



  /*
  |--------------------------------------------------------------------------
  | Open Add Sub Works
  |--------------------------------------------------------------------------
  |
  | This is called when the user clicks
  | "+ Sub Works" beside a Trade Item.
  |
  | It opens TenderDetails in "workGroup" mode.
  |
  */

  const handleAddSubWorks = (
    rateGroupNo: number,
    rateType: "Trade Item" | "Stage Item"
  ) => {
    console.log(
      "Opening Sub Works for RateGroup:",
      rateGroupNo,
      "Parent Rate Type:",
      rateType
    );

    // Keep the selected parent RateGroupNo and parent type.
    // This does not change Trade Item or Stage Item creation/saving.
    setSelectedRateGroupNo(rateGroupNo);
    setParentRateType(rateType);

    // Switch to WorkGroup mode
    setItemMode("workGroup");

    // Sub Works does not belong to
    // Contract Section selection
    setSelectedSection(null);
    setSelectedContractNode(null);

    // Open item popup
    setActiveMenu("item");

    // Directly open TradeItem WorkGroup list
    setSelectedItemType("TradeItem");
  };

  /*
  |--------------------------------------------------------------------------
  | Select Item Type
  |--------------------------------------------------------------------------
  */

  const handleItemTypeSelect = (
    type: ItemType
  ) => {
    // Remember the selected type and open its form.
    setSelectedItemType(type);
    setLastSelectedItemType(type);

    // Close the Add Item type-selection popup.
    // The selected form is rendered separately using selectedItemType,
    // so only the form remains visible.
    setActiveMenu(null);
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="relative flex h-full min-h-0 w-full overflow-hidden bg-white">

      {/* =====================================================
          MAIN CONTENT AREA
      ====================================================== */}

      <main className="min-w-0 flex-1 overflow-hidden bg-white">

        <ContractSectionTabs
          refreshKey={refreshKey}
          onAddSection={handleAddSection}
          selectedSection={selectedSection}

          onSelectSection={(node) => {
            setSelectedSection(
              node.contractNodeNo
            );

            setSelectedContractNode(
              node
            );
          }}

          /*
          |--------------------------------------------------------------------------
          | Add Item beside a section
          |--------------------------------------------------------------------------
          */

          onAddItem={handleAddItemForSection}

          /*
          |--------------------------------------------------------------------------
          | Add Sub Item beside a Trade Item
          |--------------------------------------------------------------------------
          */

          onAddSubItem={handleAddSubItem}

          /*
          |--------------------------------------------------------------------------
          | Add Sub Works beside a Trade Item
          |--------------------------------------------------------------------------
          */

          onAddSubWorks={handleAddSubWorks}
        />

      </main>

      {/* =====================================================
          ADD SECTION POPUP
      ====================================================== */}

      {activeMenu === "section" && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/20"
          onClick={closePopup}
        >

          <div
            className="relative max-h-[90%] w-[420px] overflow-auto rounded-xl bg-white shadow-xl"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* Popup Header */}

            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">

              <h2 className="text-[14px] font-semibold text-[#53538c]">
                Add Section
              </h2>

              <button
                type="button"
                onClick={closePopup}
                className="text-[20px] leading-none text-gray-400 hover:text-gray-700"
              >
                ×
              </button>

            </div>

            {/* Add Section Form */}

            <div className="p-1">

              <AddSection
                parentNode={parentNode}
                onSaved={handleSectionSaved}
              />

            </div>

          </div>
        </div>
      )}

      {/* =====================================================
          ADD ITEM TYPE POPUP
      ====================================================== */}

      {activeMenu === "item" && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/20"
          onClick={closePopup}
        >

          <div
            className="relative w-[360px] rounded-xl bg-white shadow-xl"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* Popup Header */}

            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">

              <div>

                <h2 className="text-[14px] font-semibold text-[#53538c]">
                  Add Item
                </h2>

                <div className="mt-0.5 text-[10px] text-gray-400">
                  Select Item Type
                </div>

                {/* Selected Section */}

                {selectedContractNode && (
                  <div className="mt-1 text-[10px] text-[#6d96f2]">
                    Section:{" "}
                    {selectedContractNode.contractNode}
                  </div>
                )}

              </div>

              <button
                type="button"
                onClick={closePopup}
                className="text-[20px] leading-none text-gray-400 hover:text-gray-700"
              >
                ×
              </button>

            </div>

            {/* =================================================
                ITEM TYPE OPTIONS
            ================================================= */}

            <div className="p-4">

              {/* -------------------------------------------------
                  TRADE ITEM
              ------------------------------------------------- */}

              <button
                type="button"
                onClick={() =>
                  handleItemTypeSelect(
                    "TradeItem"
                  )
                }
                className="mb-2 flex w-full items-center rounded-lg border border-gray-200 px-4 py-3 text-left transition hover:border-[#1a73e8] hover:bg-[#f5f9ff]"
              >

                <span className="mr-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#e8f0fe] text-[16px] text-[#1a73e8]">
                  +
                </span>

                <div>

                  <div className="text-[12px] font-medium text-gray-700">
                    TradeItem
                  </div>

                  <div className="mt-0.5 text-[10px] text-gray-400">
                    Add trade related item
                  </div>

                </div>

              </button>

              {/* -------------------------------------------------
                  STAGE ITEM
              ------------------------------------------------- */}

              <button
                type="button"
                onClick={() =>
                  handleItemTypeSelect(
                    "StageItem"
                  )
                }
                className="mb-2 flex w-full items-center rounded-lg border border-gray-200 px-4 py-3 text-left transition hover:border-[#1a73e8] hover:bg-[#f5f9ff]"
              >

                <span className="mr-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#e8f0fe] text-[16px] text-[#1a73e8]">
                  +
                </span>

                <div>

                  <div className="text-[12px] font-medium text-gray-700">
                    StageItem
                  </div>

                  <div className="mt-0.5 text-[10px] text-gray-400">
                    Add stage related item
                  </div>

                </div>

              </button>

              {/* -------------------------------------------------
                  PRICE ITEM
              ------------------------------------------------- */}

              <button
                type="button"
                onClick={() =>
                  handleItemTypeSelect(
                    "PriceItem"
                  )
                }
                className="flex w-full items-center rounded-lg border border-gray-200 px-4 py-3 text-left transition hover:border-[#1a73e8] hover:bg-[#f5f9ff]"
              >

                <span className="mr-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#e8f0fe] text-[16px] text-[#1a73e8]">
                  +
                </span>

                <div>

                  <div className="text-[12px] font-medium text-gray-700">
                    PriceItem
                  </div>

                  <div className="mt-0.5 text-[10px] text-gray-400">
                    Add price related item
                  </div>

                </div>

              </button>

            </div>

          </div>
        </div>
      )}

      {/* =====================================================
          SELECTED ITEM FORM POPUP
      ====================================================== */}

      {selectedItemType && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center bg-black/20"
          onClick={() =>
            setSelectedItemType(null)
          }
        >

          <div
            className="relative h-[90%] w-[90%] overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* Popup Header */}

            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">

              <div>

                <h2 className="text-[14px] font-semibold text-[#53538c]">
                  Add {selectedItemType}
                </h2>

                {selectedContractNode && (
                  <div className="mt-0.5 text-[10px] text-gray-400">
                    Section:{" "}
                    {selectedContractNode.contractNode}
                  </div>
                )}

              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedItemType(null)
                }
                className="text-[20px] leading-none text-gray-400 hover:text-gray-700"
              >
                ×
              </button>

            </div>

            {/* =================================================
                ITEM CONTENT
            ================================================= */}

            <div className="h-[calc(100%-49px)] overflow-auto">

              {/* -------------------------------------------------
                  TRADE ITEM
              ------------------------------------------------- */}

              {selectedItemType ===
                "TradeItem" && (
                <TenderDetails
                  selectedContractNode={
                    selectedContractNode
                  }
                  mode={itemMode}
                  selectedRateGroupNo={
                    selectedRateGroupNo
                  }
                   selectedWbsNo={selectedWbsNo}
                     parentRateType={parentRateType}
                  onItemsAdded={() => {
                    closePopup();

                    // Refresh main panel after
                    // Trade Item / Sub Item / Sub Works
                    // is added
                    setRefreshKey(
                      (previous) =>
                        previous + 1
                    );
                  }}
                />
              )}

              {/* -------------------------------------------------
                  STAGE ITEM
              ------------------------------------------------- */}

              {/* {selectedItemType ===
                "StageItem" && (
                <div className="flex h-full items-center justify-center">

                  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">

                    <div className="text-[14px] font-semibold text-[#53538c]">
                      StageItem
                    </div>

                    <div className="mt-2 text-[11px] text-gray-500">
                      StageItem form will be added here.
                    </div>

                  </div>

                </div> m
              )} */}
            
{selectedItemType === "StageItem" && (
  <StageItemForm
    tenderNo={tenderNumber}
    contractNode={Number(selectedContractNode?.contractNodeNo ?? 0)}
    onItemsAdded={() => {
      closePopup();
      setRefreshKey((previous) => previous + 1);
    }}
        onCancel={closePopup}

  />
)}


              {/* -------------------------------------------------
                  PRICE ITEM
              ------------------------------------------------- */}

              {selectedItemType ===
                "PriceItem" && (
                <div className="flex h-full items-center justify-center">

                  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">

                    <div className="text-[14px] font-semibold text-[#53538c]">
                      PieceItem
                    </div>

                    <div className="mt-2 text-[11px] text-gray-500">
                      PieceItem form will be added here.
                    </div>

                  </div>

                </div>
              )}

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default PageLayout;